import { Dispatch, Middleware } from '@reduxjs/toolkit'
import { ALL_ROUTES } from '@v2/domains/routing/constants/route-configs.constants'
import { RouteKey } from '@v2/domains/routing/types/routing.types'
import { buildRoute } from '@v2/domains/routing/utils/route.utils'
import { IUserSessionStore } from '@v2/domains/user/user.types'

import { cleanStringToBeUrlReadable } from 'src/default/helpers/clean-string'
import { navigationState } from 'src/v2/commons/utils/navigation'

import { RootState } from '../store'

const routerMiddleware: Middleware =
  (store) => (next: any) => (action: any) => {
    const state = store.getState() as RootState
    const userSession = state.user.userSession
    const { accessToken } = state.auth

    if (action.type === 'NAVIGATE_TO_ROUTE') {
      return handleNavigateToRoute(action, userSession, accessToken, next)
    }

    if (action.type === 'CHECK_ROUTE') {
      const result = next(action)

      if (!action.payload?.skipValidation) {
        handleCheckRoute(userSession, accessToken)
      }
      return result
    }

    return next(action)
  }

const handleNavigateToRoute = (
  action: any,
  userSession: IUserSessionStore,
  accessToken: string,
  next: Dispatch,
) => {
  const { routeKey, params, options = {} } = action.payload
  const {
    includeOrgPrefix = true,
    hardRedirect = false,
    skipValidation = false,
  } = options

  const routeConfig = ALL_ROUTES[routeKey]
  if (!routeConfig) {
    return next(action)
  }

  const fullPath = buildRoute(
    routeKey as RouteKey,
    params,
    userSession.currentOrganization?.name,
    includeOrgPrefix,
  )

  if (hardRedirect) {
    window.location.href = fullPath
    return next(action)
  }

  if (navigationState.navigate) {
    navigationState.navigate(fullPath)
  } else {
    window.location.href = fullPath
  }

  return next(action)
}

const handleCheckRoute = (
  userSession: IUserSessionStore,
  accessToken: string,
) => {
  if (!navigationState.location) {
    return
  }

  const pathname = navigationState.location.pathname

  localStorage.setItem('lastCheckedRoute', pathname)

  if (pathname === '/') {
    if (accessToken) {
      if (userSession.currentOrganization?.id) {
        const path = `/${cleanStringToBeUrlReadable(
          userSession.currentOrganization.name,
        )}/leads`
        navigate(path)
        return
      }
      navigate('/select-organization')
      return
    }
    navigate('/login')
    return
  }

  const isPublicRoute = Object.values(ALL_ROUTES)
    .filter((route) => !route.requiresAuth)
    .some((route) => {
      const routePath = route.path.startsWith('/')
        ? route.path
        : `/${route.path}`
      return pathname === routePath || pathname.endsWith(routePath)
    })

  if (!accessToken && !isPublicRoute) {
    navigate('/login')
    return
  }

  if (
    accessToken &&
    !userSession.currentOrganization.id &&
    !pathname.includes('select-organization') &&
    !isPublicRoute
  ) {
    navigate('/select-organization')
    return
  }

  if (
    accessToken &&
    userSession.currentOrganization.id &&
    (pathname.includes('login') || pathname.includes('select-organization'))
  ) {
    const path = `/${cleanStringToBeUrlReadable(
      userSession.currentOrganization.name,
    )}/leads`
    navigate(path)
    return
  }
}

const navigate = (path: string) => {
  if (navigationState.navigate) {
    navigationState.navigate(path)
  } else {
    console.warn('navigationState.navigate is not avaialbe')
    window.location.href = path
  }
}

export default routerMiddleware
